import { useContext } from 'react';
import { useQuery } from 'react-query';
import { ServerErrorResponse, baseApiUrl, fetcher, createParamString } from 'data_sources/constants';
import { AppContext } from 'views/App/context';
import { SingleOrgMetricsApiResponse, SingleOrgMetricsResponse } from '../_types';
import { generatePolicyMetrics } from './riskPoolOrgDataGenerator';
import { getMetricStatus, getPendingUpdatesStatus } from '../_constants';
import { useRiskPoolDemoData } from '../../../../_data_sources/useRiskPoolDemoData';

interface QueryParams {
  date: string;
}

const transformResponse = (api: SingleOrgMetricsApiResponse): SingleOrgMetricsResponse => ({
  completedPolicyAcks: {
    ...api.completedPolicyAcks,
    status: getMetricStatus(api.completedPolicyAcks.percent * 100),
  },
  completedDtbs: {
    ...api.completedDtbs,
    status: getMetricStatus(api.completedDtbs.percent * 100),
  },
  pendingPolicyUpdates: {
    count: api.pendingPolicyUpdates,
    status: getPendingUpdatesStatus(api.pendingPolicyUpdates),
  },
  policiesInIssuedStatus: {
    percent: api.policiesInIssuedStatus.percent ?? 0,
    count: api.policiesInIssuedStatus.count ?? 0,
    totalCount: api.policiesInIssuedStatus.totalCount ?? 0,
  },
  dtbAssignmentsCreated: {
    ...api.dtbAssignmentsCreated,
    status: getMetricStatus(api.dtbAssignmentsCreated.percent * 100),
  },
});

export const getPolicyMetrics = async (
  parentOrgId: string,
  childOrgId: string,
  params: QueryParams,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<SingleOrgMetricsResponse> => {
  const url = `${baseApiUrl()}/activity-center/v1/organizations/${parentOrgId}/risk-pool/related-organizations/${childOrgId}/policy/metrics`;
  const paramString = createParamString({ date: params.date });

  try {
    const apiData: SingleOrgMetricsApiResponse = await fetcher({
      method: 'get',
      url: `${url}?${paramString}`,
      options: { signal },
    });
    return transformResponse(apiData);
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export default function usePolicyMetrics(
  childOrgId: string,
  params: QueryParams,
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const { currentOrganizationId } = useContext(AppContext);
  const parentOrgId = currentOrganizationId || childOrgId;
  const isDemoDataEnabled = useRiskPoolDemoData();

  return useQuery<SingleOrgMetricsResponse, Error>(
    ['risk_pool_org_policy_metrics', parentOrgId, childOrgId, params.date],
    async ({ signal }): Promise<SingleOrgMetricsResponse> => {
      if (isDemoDataEnabled) {
        return generatePolicyMetrics(childOrgId, params.date);
      }
      return getPolicyMetrics(parentOrgId, childOrgId, params, signal, onError);
    },
    {
      ...options,
      enabled: !!childOrgId && !!params.date,
    }
  );
}
